import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { NotFoundError } from '@/lib/errors';
import { useAuthStore } from '@store/authStore';
import { getOnboardingResumeTarget, readPendingInviteFromSession, hasRealOrgId } from '@/utils/onboardingResume';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { RiShieldCheckLine, RiAlertLine, RiCheckboxCircleLine, RiQrCodeLine, RiKeyLine, RiArrowRightLine } from '@remixicon/react';
import { Alert, AlertDescription } from '@components/ui/Alert';

export default function MFAEnroll() {
  const navigate = useNavigate();
  const [mfaCode, setMfaCode] = useState('');
  const [mfaEnrollData, setMfaEnrollData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [enrolledFactorId, setEnrolledFactorId] = useState(null);
  const initialized = useRef(false);

  useEffect(() => {
    // Prevent double-execution in React Strict Mode
    if (initialized.current) return;
    initialized.current = true;

    initializeMFAEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeMFAEnrollment = async () => {
    setLoading(true);
    try {
      // Refresh session to ensure it's valid
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError || !session) {
        setError('Session expired. Please sign in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      // Onboarding gate — never enroll MFA for a user whose BE profile
      // isn't ACTIVE yet. This was the source of the "MFA Setup Error
      // Forbidden" + "MFA enrolled successfully" dual-toast bug: users
      // arrived here from EmailConfirmation before they had a BE
      // profile row, so the profile-side calls 403'd while the
      // Supabase-side enroll succeeded. Route them back through the
      // resume helper so they land at the correct onboarding step.
      try {
        const profile = await apiClient.getUserByUserId(session.user.id).catch(() => null);
        const organization = hasRealOrgId(profile)
          ? await apiClient.getOrganizationById(profile.org_id).catch(() => null)
          : null;
        const target = getOnboardingResumeTarget(profile, organization, {
          pendingInvite: readPendingInviteFromSession(session),
        });
        if (target.path !== '/dashboard') {
          navigate(target.path);
          return;
        }
      } catch (gateErr) {
        // Gate lookup failed — fail-safe to /onboarding so we don't
        // MFA-enroll a user we can't verify is ACTIVE.
        console.error('MFAEnroll: pre-flight resume check failed', gateErr);
        navigate('/onboarding');
        return;
      }

      // Check for existing MFA factors
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();

      if (!listError && factors && factors.totp && factors.totp.length > 0) {
        // Check if any factor is verified
        const verifiedFactor = factors.totp.find(f => f.status === 'verified');
        if (verifiedFactor) {
          setMessage('MFA is already set up for your account.');

          // Check if user has completed profile
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            try {
              await apiClient.getUserByUserId(user.id);
              setTimeout(() => navigate('/dashboard'), 1500);
            } catch (err) {
              if (err instanceof NotFoundError || err.statusCode === 404) {
                setTimeout(() => navigate('/profile-setup'), 1500);
              }
            }
          }
          setLoading(false);
          return;
        }

        // Found unverified factors - unenroll them first
        for (const factor of factors.totp) {
          if (factor.status !== 'verified') {
            try {
              const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
              if (unenrollError) {
                console.warn(`Failed to clean up existing MFA factor ${factor.id}:`, unenrollError.message);
                // Continue anyway - the factor might be stale
              }
            } catch (cleanupErr) {
              console.warn(`Error cleaning up MFA factor ${factor.id}:`, cleanupErr);
              // Continue anyway - we'll create a new one
            }
          }
        }
      }

      // Proceed with new enrollment (either no factors existed, or we cleaned up unverified ones)
      await enrollMFA();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const enrollMFA = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use a unique friendly name. Supabase requires friendly names to be
      // unique per user, and stale unverified factors with the same name can
      // linger without showing up in listFactors() — causing every retry to
      // hit mfa_factor_name_conflict. A unique suffix makes enrollment robust.
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Authenticator App ${Date.now()}`,
      });

      if (error) {
        throw new Error(`MFA enrollment failed: ${error.message}`);
      }

      if (data) {
        // Supabase returns qr_code as either a data URI ("data:image/svg+xml;...")
        // or as a raw SVG string ("<?xml...><svg...>"). Normalize to a data URI
        // so <img src> renders in both cases.
        const rawQr = data.totp.qr_code;
        const qrCode = rawQr.startsWith('data:')
          ? rawQr
          : `data:image/svg+xml;utf8,${encodeURIComponent(rawQr)}`;
        setMfaEnrollData({
          qrCode,
          secret: data.totp.secret,
        });
        setEnrolledFactorId(data.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyMFAEnrollment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!enrolledFactorId) {
        throw new Error('No factor ID found. Please re-enroll.');
      }

      // Validate code format
      const code = mfaCode.trim();
      if (!/^\d{6}$/.test(code)) {
        throw new Error('Please enter a valid 6-digit code');
      }

      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: enrolledFactorId,
      });

      if (challengeError) {
        // Factor expired or was cleaned up — re-enroll and prompt user to scan again
        if (challengeError.code === 'mfa_factor_not_found') {
          setMfaEnrollData(null);
          setEnrolledFactorId(null);
          setMfaCode('');
          await enrollMFA();
          setError('Enrollment expired. Please scan the new QR code and enter a fresh 6-digit code.');
          return;
        }
        throw new Error(`Challenge failed: ${challengeError.message}`);
      }

      if (!challengeData || !challengeData.id) {
        throw new Error('Failed to create challenge. Please try again.');
      }

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: enrolledFactorId,
        challengeId: challengeData.id,
        code: code,
      });

      if (verifyError) {
        // Wrong/expired code — surface the error but leave the factor intact
        // so the user can retry with a fresh code. Unenrolling here would force
        // a full re-scan on every typo.
        setError(verifyError.message || 'Invalid code. Please try again.');
        setMfaCode('');
        return;
      }

      setMessage('MFA enrolled successfully!');

      // Refresh session to ensure token is available with AAL2
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error("Error refreshing session after MFA:", refreshError);
      }

      // Wait a moment for the session to propagate
      await new Promise(resolve => setTimeout(resolve, 300));

      // Sync auth store to ensure API client has fresh token
      await useAuthStore.getState().syncWithSupabase();

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not found after MFA enrollment');
      }

      // Check if user has completed profile
      try {
        // Use getUserByUserId to check if current user has a profile
        await apiClient.getUserByUserId(user.id);
        // User profile exists in backend, go to dashboard
        navigate('/dashboard');
      } catch (err) {
        // Only redirect to profile setup if user profile doesn't exist (404)
        if (err instanceof NotFoundError || err.statusCode === 404) {
          navigate('/profile-setup');
        } else {
          // For other errors (network, server, etc.), throw to outer catch
          throw err;
        }
      }
    } catch (err) {
      // Only post-verify failures (session refresh, profile lookup, navigation)
      // reach here now — wrong-code errors are handled inline above. The factor
      // is already verified at this point, so we must NOT unenroll it.
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <RiShieldCheckLine className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-primary-500 mb-2">Setup Two-Factor Authentication</h2>
          <p className="text-gray-600">
            Secure your account with an authenticator app
          </p>
        </div>

        {error && (
          <Alert className="border-red-200 bg-red-50 mb-3">
            <RiAlertLine className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <p>MFA Setup Error</p>
              <p>{error}</p>
            </AlertDescription>
          </Alert>
        )}

        {message && (
          <Alert className="border-green-200 bg-green-50 mb-3">
            <RiCheckboxCircleLine className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <p>Success</p>
              <p>{message}</p>
            </AlertDescription>
          </Alert>
        )}

        <div className="bg-white">
          {loading && !mfaEnrollData && (
            <div className="py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
              <p className="text-gray-600">Setting up MFA...</p>
            </div>
          )}

          {mfaEnrollData && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center mb-4">
                  <RiQrCodeLine className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Scan QR Code</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Use your authenticator app (Google Authenticator, Authy, etc.) to scan this QR code:
                </p>
                <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                  <img
                    src={mfaEnrollData.qrCode}
                    alt="QR Code for MFA Setup"
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <RiKeyLine className="h-4 w-4 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Manual Entry Key</span>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  If you can't scan the QR code, enter this key manually:
                </p>
                <div className="bg-white rounded border p-2">
                  <code className="text-sm font-mono text-gray-800 break-all">
                    {mfaEnrollData.secret}
                  </code>
                </div>
              </div>
            </div>
          )}
          {mfaEnrollData && (
            <form onSubmit={verifyMFAEnrollment} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700">
                  Verification Code <span className="text-red-500">*</span>
                </label>
                <Input
                  id="mfaCode"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  required
                  maxLength={6}
                  disabled={loading}
                  className="h-12 text-center text-lg font-mono tracking-widest bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary-500 disabled:opacity-50"
                />
                <p className="text-xs text-gray-500">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
              
              <Button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="w-full h-12 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <RiShieldCheckLine className="mr-2 h-4 w-4" />
                    Verify & Enable MFA
                    <RiArrowRightLine className="ml-2 h-4 w-4" />
                  </div>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
